import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { CustomCompType, GenericField, GenericFormType } from '@neo/blocks/forms/types'
import { Alert } from '@neo/primitives/alert'
import { Checkbox } from '@neo/primitives/checkbox'
import { InputWithSelect } from '@neo/primitives/input-with-select'
import { RadioGroupComponent } from '@neo/primitives/radio-group'
import { Select } from '@neo/primitives/select'
import { Tooltip } from '@neo/primitives/tooltip'
import { AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import { MultiFlowSelect } from 'v2/shared/components/flow-select'
import { documentSetupSchema } from '../config'
import type { KBRetrievalProfile, NewKBFormValues } from '../types'
// Field Configuration

export const SyncFrequencySelect = ({
  value,
  form,
  ...rest
}: {
  value: { sync_period: string; sync_frequency: number }
  form: GenericFormType<NewKBFormValues>
}) => {
  const newValue = {
    value: value?.sync_frequency,
    option: value?.sync_period,
  }
  const placeText = `Enter Value between 1 - 3`
  const [syncFrequencyEnabled, setSyncFrequencyEnabled] = useState(form.state.values.sync_enabled)
  const onSyncEnabledChange = (checked: boolean) => {
    setSyncFrequencyEnabled(!checked)
    form.setFieldValue('sync_enabled', !checked)
  }
  return (
    <div className="w-full">
      <div className="flex items-center gap-4">
        <InputWithSelect
          {...rest}
          value={newValue}
          placeholder={placeText}
          disabled={!syncFrequencyEnabled}
        />
        <Checkbox
          label="Do not sync automatically"
          name="sync_enabled"
          value={!syncFrequencyEnabled}
          className="mt-4"
          onChange={onSyncEnabledChange}
        />
      </div>
      <span className="text-tertiary text-s-500">
        We&apos;ll automatically check and update the knowledge base at this interval.
      </span>
    </div>
  )
}

// // --- MediaProcessingSection ---
const mediaProcessingFields: GenericField<NewKBFormValues>[] = [
  {
    name: 'ignore_image_content',
    componentType: 'checkbox',
    zodValidation: documentSetupSchema.shape.ignore_image_content,
    inputProps: {
      label: 'Exclude images from processing',
      'data-testid': 'checkbox-ignore-image-content',
      labelClassName: 'text-s-500 text-primary',
    },
  },
  {
    name: 'ignore_youtube_content',
    componentType: 'checkbox',
    zodValidation: documentSetupSchema.shape.ignore_youtube_content,
    inputProps: {
      label: 'Exclude Youtube videos from processing',
      'data-testid': 'checkbox-ignore-youtube-content',
      labelClassName: 'text-s-500 text-primary',
    },
  },
]
const MediaProcessingSection = ({ form }: { form: GenericFormType<NewKBFormValues> }) => (
  <div className="bg-bg3 rounded-md p-3 grid gap-4 grid-cols-2">
    <div className="flex flex-col gap-3 col-span-2">
      <h2 className="text-p-500 mb-2">Media processing</h2>
      <FieldsRenderer<NewKBFormValues> form={form} fields={mediaProcessingFields} />
    </div>
    <div className="flex items-center gap-1 text-secondary col-span-2">
      <span className="text-xsi-400">
        You can change this later from the knowledge base&apos;s settings; it takes effect on the
        next sync.
      </span>
    </div>
  </div>
)

// --- SyncConfigurationSection ---
const SyncConfigurationSection = ({ form }: { form: GenericFormType<NewKBFormValues> }) => (
  <div>
    <h2 className="text-p-500 mb-2">Content configuration</h2>
    <section className="bg-bg2 rounded-xl p-6 flex flex-col gap-4">
      <RadioGroupComponent
        name="url_selection_type"
        options={[
          { label: 'Add all sub-pages from the source', value: 'all' },
          {
            label: 'I want to customize and select the pages to add from the source',
            value: 'selected',
          },
        ]}
        value={form.state.values.url_selection_type}
        onChange={(value) => {
          form.setFieldValue('url_selection_type', value as 'all' | 'selected')
        }}
        data-testid="radio-sync-configuration"
        optionsClassName="flex flex-col gap-3 "
        labelClassName="text-s-500 text-primary"
      />
      <div className="flex items-center gap-1 text-secondary">
        <Tooltip content="This setting can't be changed later for this Knowledge base.">
          <AlertTriangle className=" w-4 h-4" data-testid="icon-warning" />
        </Tooltip>
        <span className="text-xsi-400">
          This setting can&apos;t be changed later for this Knowledge base.
        </span>
      </div>
    </section>
  </div>
)

// --- RetrievalModeSection ---
// Chooses the KB's retrieval `profile`. When "Docs" is picked we surface a warning: docs mode
// raises retrieval latency (top-20 + long-query rewrite) so it belongs on the chat channel, not on
// a voice agent. Reads the value reactively via form.useStore so the warning shows/hides on change.
const RetrievalModeSection = ({ form }: { form: GenericFormType<NewKBFormValues> }) => {
  const profile = form.useStore((s) => s.values.profile)
  return (
    <div>
      <h2 className="text-p-500 mb-2">Retrieval mode</h2>
      <Select
        name="profile"
        value={profile}
        options={[
          { value: 'default', label: 'Default' },
          { value: 'docs', label: 'Docs' },
        ]}
        onChange={(value) => form.setFieldValue('profile', value as KBRetrievalProfile)}
        placeholder="Select retrieval mode"
      />
      <span className="text-tertiary text-s-500">
        Default suits most knowledge bases. Docs mode is tuned for large documentation corpora.
      </span>
      {profile === 'docs' ? (
        <div data-testid="warning-docs-mode" className="mt-3">
          <Alert
            variant="warning"
            content="Use Docs mode only for documentation on the chat channel — not for voice agents (retrieval latency is higher)."
          />
        </div>
      ) : null}
    </div>
  )
}

export const documentSetupFields = (
  // Gates the retrieval-mode (Default/Docs) dropdown behind `kbDocsModeEnabled`. Only orgs with the
  // flag on (e.g. plivo) can pick a retrieval profile; everyone else never sees the field.
  retrievalModeEnabled: boolean,
): GenericField<NewKBFormValues>[] => [
  {
    name: 'name',
    componentType: 'input',
    zodValidation: documentSetupSchema.shape.name,
    inputProps: {
      label: 'Knowledge base name',
      placeholder: 'Enter knowledge base name',
      'data-testid': 'input-knowledge-base-name',
    },
  },
  {
    name: 'agents',
    type: 'custom',
    component: MultiFlowSelect as CustomCompType<NewKBFormValues>,
    eventDataExtractor: (e) => e as string[],
    zodValidation: documentSetupSchema.shape.agents,
    inputProps: {
      label: 'Associated Agent Flows',
      placeholder: 'Select agents',
      'data-testid': 'select-kb-agents',
    },
  },
  {
    name: 'associated_agents_hr',
    type: 'slot',
    component: <hr className="mb-2" />,
  },
  // Retrieval-mode dropdown + its separator are included ONLY when the flag is on. Omitting the
  // field (rather than hiding it) leaves `profile` at its schema default ('default'), so a gated-off
  // org still creates a valid KB with the voice-friendly default profile.
  ...((retrievalModeEnabled
    ? [
        {
          name: 'profile',
          type: 'custom',
          zodValidation: documentSetupSchema.shape.profile,
          component: RetrievalModeSection as CustomCompType<NewKBFormValues>,
          eventDataExtractor: (data) => ({
            profile: data.value,
          }),
        },
        {
          name: 'profile_hr',
          type: 'slot',
          component: <hr className="mb-2" />,
        },
      ]
    : []) as GenericField<NewKBFormValues>[]),
  {
    name: 'media_processing',
    type: 'basic',
    component: ({ form }) => <MediaProcessingSection form={form} />,
  },
  {
    name: 'media_processing_hr',
    type: 'slot',
    component: <hr className="mb-2" />,
  },
  {
    name: 'notes',
    type: 'slot',
    component: (
      <div>
        <h2 className="text-pi-400 mb-2">
          These configurations will apply when the knowledge source is Website and App
        </h2>
      </div>
    ),
  },
  {
    name: 'sync_frequency',
    type: 'custom',
    zodValidation: documentSetupSchema.shape.sync_frequency,
    component: SyncFrequencySelect as CustomCompType<NewKBFormValues>,
    inputProps: {
      label: 'Sync data once every',
      placeholder: 'Select sync frequency',
      options: [
        { value: 'WEEK', label: 'weeks' },
        { value: 'MONTH', label: 'months' },
      ],
    },
    eventDataExtractor: (data) => ({
      sync_period: data.option,
      sync_frequency: data.value,
    }),
  },
  {
    name: 'url_selection_type',
    type: 'custom',
    zodValidation: documentSetupSchema.shape.url_selection_type,
    component: SyncConfigurationSection as CustomCompType<NewKBFormValues>,
    eventDataExtractor: (data) => ({
      url_selection_type: data.value,
    }),
  },
  {
    name: 'final_hr',
    type: 'slot',
    component: <hr className="mb-2" />,
  },
]
